/**
 * SARKY Wishlist page -- resolves the saved product handles (from the
 * shared browser-local wishlist, assets/sarky-wishlist.js) into REAL,
 * server-rendered Collection product cards.
 *
 * STOP DUPLICATING PRODUCT CARD LOGIC: earlier passes rebuilt the card's
 * image/gallery/swatches/title/price markup client-side from raw product
 * JSON, which meant every visual/behavioral difference between this page
 * and Collection had to be independently discovered and independently
 * fixed -- exactly the drift Billy's UAT kept catching (swatches that
 * looked different, weren't clickable, didn't behave like Collection).
 *
 * This file now fetches the ACTUAL Collection `<product-card>` HTML for
 * each saved product via Shopify's native Section Rendering API, against
 * a hidden, dedicated data-source template that renders the exact same
 * `_product-card` block chain (card gallery, swatches, title, price)
 * Collection uses, with the exact same settings
 * (templates/collection.json's own "product-card" block, copied verbatim
 * into templates/product.sarky-wishlist-card.json):
 *
 *   /products/{handle}?view=sarky-wishlist-card&section_id={sectionId}
 *
 * `sectionId` is the runtime, theme-instance-specific compound id
 * Shopify assigns that template's section (e.g.
 * "template--123456__sarky_wishlist_product_card") -- NOT hardcoded here
 * (it would break on every theme duplicate/publish), but discovered once
 * per page load from the first full-page fetch of that same alternate
 * template, then reused for every other saved product via the
 * lightweight Section Rendering API.
 *
 * The returned HTML is spliced directly into the grid. No swatch, image-
 * swap, gallery, or spacing logic is reimplemented here: the instant this
 * markup is in the DOM, assets/product-card.js's `<product-card>` /
 * `swatches-variant-picker-component` custom elements (already loaded
 * sitewide, snippets/scripts.liquid) upgrade automatically and drive
 * swatch clicks, image switching, and navigation exactly as they do on
 * Collection -- custom element upgrade does not depend on how the markup
 * got into the document. assets/sarky-card-gallery.js (swipe/dots) and
 * assets/sarky-card-swatch-guard.js (swatch-row click guard) are loaded
 * directly by sections/sarky-wishlist.liquid for the same reason: their
 * own embedded <script> tags inside the fetched HTML are inert (inserted
 * via innerHTML, browsers never execute those), so they're loaded once,
 * up front, instead -- both are already delegated/MutationObserver-driven
 * so this is the only wiring they need.
 *
 * A stored handle that no longer resolves (deleted/unpublished product)
 * is pruned from the wishlist automatically and safely -- never rendered
 * as a blank card, fake image, or broken link.
 *
 * PRESERVE SAVED WISHLIST VARIANT / SIZE (this pass): each saved item now
 * carries the exact variant id the visitor chose when saving (schema v2,
 * assets/sarky-wishlist.js). That id is passed straight through as
 * `&variant={id}` on the same fetch above -- Shopify's own Liquid variant
 * resolution (`product.selected_variant` / `product_option_value.variant`)
 * does the rest natively for the INITIAL render: the correct color swatch
 * pre-selects, the card's link points at that exact variant, and -- proven
 * live -- every OTHER color swatch's own target variant resolves to the
 * SAME size too, because that resolution already accounts for the other
 * currently-selected option (size) carried in via `?variant=`. None of
 * that is custom code.
 *
 * A LATER color click on the card is a different story: the swatch
 * component (assets/variant-picker.js) only remembers option values it can
 * find as `fieldset input:checked` inside itself, and the card never
 * renders a Size input at all (blocks/swatches.liquid only ever renders
 * Color) -- so a plain color click would silently drop back to that
 * color's own first-available size. injectPreservedSizeInput() below
 * closes that one gap by adding a hidden, pre-checked input carrying the
 * preserved Size's real option-value id (rendered once by
 * sections/sarky-wishlist-product-card.liquid's own marker) into that same
 * fieldset -- using the component's own existing, unmodified data
 * contract, not a second variant-resolution system. Picking a different
 * color then fires the same native `variant:update` event Collection's
 * `<product-card>` already dispatches after its own variant-change fetch
 * -- confirmed live that this does NOT bubble past the
 * swatches-variant-picker-component itself here (some ancestor listener in
 * that same shared chain stops it), so bindVariantUpdateListener() below
 * listens directly on each card's own component instead of the grid. It
 * persists the resulting variant id via `SarkyWishlist.updateVariant()`
 * and re-injects the preserved-size marker so the NEXT color click keeps
 * working too. No second swatch/variant system.
 */
(function () {
  'use strict';

  var ALTERNATE_VIEW = 'sarky-wishlist-card';
  var SECTION_KEY = 'sarky_wishlist_product_card';

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  function whenWishlistReady(callback) {
    if (window.SarkyWishlist) {
      callback();
      return;
    }

    // Defense in depth: the shared module (assets/sarky-wishlist.js) is
    // loaded in <head>, ahead of this page's own content, specifically so
    // it is always ready before this runs. This listener exists only to
    // survive a future regression of that ordering rather than silently
    // rendering nothing.
    window.addEventListener('sarky:wishlist:ready', callback, { once: true });
  }

  ready(function () {
    var root = document.querySelector('.sarky-wishlist');

    if (!root) {
      return;
    }

    whenWishlistReady(initWishlistPage);
  });

  function initWishlistPage() {
    var loadingEl = document.querySelector('[data-sarky-wishlist-loading]');
    var emptyEl = document.querySelector('[data-sarky-wishlist-empty]');
    var gridEl = document.querySelector('[data-sarky-wishlist-grid]');

    // Discovered once per page load from the first full-page fetch (see
    // fetchProductCardBootstrap below), then reused for every subsequent
    // product via the lightweight Section Rendering API. Never hardcoded
    // -- this id is specific to this theme instance and would silently
    // break the moment the theme is duplicated or republished under a
    // different internal id.
    var sectionId = null;

    function showEmpty() {
      if (loadingEl) loadingEl.hidden = true;
      if (gridEl) gridEl.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
    }

    function showGrid() {
      if (loadingEl) loadingEl.hidden = true;
      if (emptyEl) emptyEl.hidden = true;
      if (gridEl) gridEl.hidden = false;
    }

    // Parses once and returns both pieces this page needs from a card
    // response: the real <product-card> element, and (when present) the
    // preserved-size marker's option value id -- see the doc comment on
    // sections/sarky-wishlist-product-card.liquid's own marker output.
    function parseCardResponse(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var marker = doc.querySelector('[data-sarky-wishlist-preserved-option-value-id]');

      return {
        card: doc.querySelector('product-card'),
        preservedValueId: marker ? marker.getAttribute('data-sarky-wishlist-preserved-option-value-id') : null,
      };
    }

    // PRESERVE SAVED WISHLIST VARIANT / SIZE: the card's own swatch picker
    // (blocks/swatches.liquid, unmodified) only ever renders Color as
    // swatches -- assets/variant-picker.js's selectedOptionsValues getter
    // reads `fieldset input:checked`, so a Size that was never rendered as
    // an input can't be "remembered" by a color click on its own. This
    // injects one hidden, pre-checked input carrying the preserved
    // non-Color option's real value id into that SAME fieldset, using the
    // swatch component's own existing data contract -- not a second
    // variant-resolution system. A no-op if the card has no swatch picker
    // (no colors) or no preserved value id is known.
    function injectPreservedSizeInput(cardElement, preservedValueId) {
      if (!cardElement || !preservedValueId) {
        return;
      }

      var fieldset = cardElement.querySelector('swatches-variant-picker-component fieldset');

      if (!fieldset) {
        return;
      }

      var existing = fieldset.querySelector('[data-sarky-wishlist-preserved-size-input]');

      if (existing) {
        existing.remove();
      }

      var input = document.createElement('input');
      input.type = 'radio';
      input.checked = true;
      input.hidden = true;
      input.setAttribute('data-option-value-id', preservedValueId);
      input.setAttribute('data-sarky-wishlist-preserved-size-input', '');
      fieldset.appendChild(input);
    }

    function extractSectionId(html) {
      var match = html.match(/shopify-section-(template--[^"'\s]*__[^"'\s]*)/);

      if (!match) {
        return null;
      }

      var candidate = match[1];

      // Sanity-check it's genuinely OUR section and not some other
      // section on the page that happens to match the broad pattern --
      // the alternate template only ever contains the one section, but
      // this keeps the discovery honest rather than assuming position.
      return candidate.indexOf(SECTION_KEY) !== -1 ? candidate : null;
    }

    function variantQueryParam(variantId) {
      return variantId ? '&variant=' + encodeURIComponent(variantId) : '';
    }

    // Bootstrap: one full-page fetch of the alternate template, which
    // both resolves THIS product's own card AND discovers the runtime
    // section id for every other product this page load.
    function fetchProductCardBootstrap(handle, variantId) {
      return fetch('/products/' + encodeURIComponent(handle) + '?view=' + ALTERNATE_VIEW + variantQueryParam(variantId)).then(function (response) {
        if (!response.ok) {
          throw new Error('Not found: ' + handle);
        }

        return response.text();
      }).then(function (html) {
        var discoveredId = extractSectionId(html);

        if (discoveredId) {
          sectionId = discoveredId;
        }

        var parsed = parseCardResponse(html);

        if (!parsed.card) {
          throw new Error('No product-card in response for ' + handle);
        }

        return parsed;
      });
    }

    // Lightweight path once the section id is known: the Section
    // Rendering API returns just that one section's HTML instead of a
    // full page.
    function fetchProductCardViaSection(handle, variantId) {
      return fetch(
        '/products/' + encodeURIComponent(handle) + '?view=' + ALTERNATE_VIEW + '&section_id=' + encodeURIComponent(sectionId) + variantQueryParam(variantId)
      ).then(function (response) {
        if (!response.ok) {
          throw new Error('Not found: ' + handle);
        }

        return response.text();
      }).then(function (html) {
        var parsed = parseCardResponse(html);

        if (!parsed.card) {
          throw new Error('No product-card in response for ' + handle);
        }

        return parsed;
      });
    }

    function fetchProductCard(handle, variantId) {
      if (sectionId) {
        return fetchProductCardViaSection(handle, variantId);
      }

      return fetchProductCardBootstrap(handle, variantId);
    }

    // PRESERVE SAVED WISHLIST VARIANT / SIZE: confirmed live that
    // 'variant:update' (dispatched by swatches-variant-picker-component
    // after its own color-change fetch) does NOT bubble past the
    // component itself here, despite `bubbles: true` in its constructor
    // (assets/events.js) -- some ancestor listener in the shared
    // component chain (assets/product-card.js / assets/variant-picker.js,
    // both left untouched) stops it. Rather than guess why or touch that
    // shared code, this listens directly on each card's OWN swatch
    // component, discovered fresh after every render -- a plain,
    // supported DOM event target, not a private API.
    function bindVariantUpdateListener(item) {
      var picker = item.querySelector('swatches-variant-picker-component');

      if (!picker) {
        return;
      }

      picker.addEventListener('variant:update', function (event) {
        var detail = event.detail || {};
        var newVariantId = detail.resource && detail.resource.id;
        var handle = item.getAttribute('data-sarky-wishlist-handle');

        if (newVariantId && handle) {
          window.SarkyWishlist.updateVariant(handle, newVariantId);
        }

        // The component just morphed this card's fieldset to its own
        // fresh server response, which wipes any earlier injected input
        // (see injectPreservedSizeInput's doc comment) -- re-inject from
        // the cached value so the NEXT color click also preserves size,
        // not just this first one.
        var cachedPreservedValueId = item.getAttribute('data-sarky-wishlist-preserved-option-value-id');
        injectPreservedSizeInput(item, cachedPreservedValueId);
      });
    }

    function renderCard(handle, parsed) {
      if (!gridEl) {
        return;
      }

      injectPreservedSizeInput(parsed.card, parsed.preservedValueId);

      var item = document.createElement('li');
      item.className = 'sarky-wishlist__item';
      item.setAttribute('data-sarky-wishlist-item', '');
      item.setAttribute('data-sarky-wishlist-handle', handle);

      // Cached here (not just used once) so a LATER native color-change
      // re-render (see bindVariantUpdateListener above) can re-inject the
      // same preserved size again -- the swatch component's own morph
      // replaces the fieldset contents with its fresh server response,
      // which has no way to know about this marker, so it doesn't survive
      // on its own.
      if (parsed.preservedValueId) {
        item.setAttribute('data-sarky-wishlist-preserved-option-value-id', parsed.preservedValueId);
      }

      item.appendChild(parsed.card);
      bindVariantUpdateListener(item);
      gridEl.appendChild(item);
    }

    function fetchOne(wishlistItem) {
      return fetchProductCard(wishlistItem.handle, wishlistItem.variantId)
        .then(function (parsed) {
          return { ok: true, handle: wishlistItem.handle, parsed: parsed };
        })
        .catch(function () {
          return { ok: false, handle: wishlistItem.handle };
        });
    }

    function resolveAndRender() {
      var wishlistItems = window.SarkyWishlist.getAllItems();

      if (wishlistItems.length === 0) {
        showEmpty();
        return;
      }

      if (gridEl) {
        gridEl.innerHTML = '';
      }

      var firstItem = wishlistItems[0];
      var restItems = wishlistItems.slice(1);

      // The first request is awaited on its own before the rest fire --
      // ONLY when sectionId isn't known yet, since resolving it is a
      // side effect of that first fetch (see fetchProductCardBootstrap).
      // Once known (every call after the first resolveAndRender(), or
      // every request beyond the first one in THIS call), everything
      // just runs in parallel as normal.
      var firstPromise = fetchOne(firstItem);
      var restPromise = sectionId
        ? Promise.all(restItems.map(fetchOne))
        : firstPromise.then(function () {
            return Promise.all(restItems.map(fetchOne));
          });

      Promise.all([firstPromise, restPromise]).then(function (values) {
        var results = [values[0]].concat(values[1]);
        var renderedCount = 0;

        results.forEach(function (result) {
          if (result.ok) {
            renderCard(result.handle, result.parsed);
            renderedCount += 1;
          } else {
            // Stale/unresolvable stored handle -- prune it safely
            // rather than rendering a blank/broken card.
            window.SarkyWishlist.remove(result.handle);
          }
        });

        if (renderedCount === 0) {
          showEmpty();
        } else {
          showGrid();
        }

        // Every card just inserted is, by definition, already saved --
        // this is the shared, authoritative way to reflect that on its
        // heart (fill state, aria-pressed, labels), not hand-rolled here.
        if (window.SarkyWishlist.syncAllHearts) {
          window.SarkyWishlist.syncAllHearts(gridEl || document);
        }
      });
    }

    resolveAndRender();

    window.addEventListener(window.SarkyWishlist.CHANGE_EVENT, function (event) {
      var detail = event.detail || {};

      if (detail.source === 'storage') {
        // Cross-tab change -- could be an addition as well as a removal,
        // so a full rebuild is the only way to pick up a newly-saved
        // product from another tab.
        resolveAndRender();
        return;
      }

      // Same-page heart click (any card's own bookmark, including this
      // grid's own cards -- clicking an already-saved heart un-saves it,
      // same toggle behavior as everywhere else). Wishlist-specific
      // behavior: drop the now-unwishlisted card from THIS grid
      // immediately, matching the previous dedicated "remove" control's
      // instant behavior. Collection's own product-card never needs this
      // at all (it has no concept of "remove me from the grid I'm in"),
      // so this stays here rather than in the shared module.
      if (!gridEl) {
        return;
      }

      var currentIds = window.SarkyWishlist.getAll();
      var items = gridEl.querySelectorAll('[data-sarky-wishlist-item]');

      items.forEach(function (item) {
        var handle = item.getAttribute('data-sarky-wishlist-handle');

        if (handle && currentIds.indexOf(handle) === -1) {
          item.remove();
        }
      });

      if (gridEl.children.length === 0) {
        showEmpty();
      }
    });
  }
})();
